import { useLayoutEffect, type RefObject } from "react";
import { TOOLBAR_TIERS, type ToolbarTier } from "./documentToolbarTiers";

/** The attributes a row carries at each tier — cumulative, so "medium and up" is one selector. */
const TIER_ATTRIBUTES: Record<ToolbarTier, readonly string[]> = {
  compact: [],
  medium: ["data-tier-medium"],
  labels: ["data-tier-medium", "data-tier-labels"],
  full: ["data-tier-medium", "data-tier-labels", "data-tier-full"],
};

export const applyToolbarTier = (row: HTMLElement, tier: ToolbarTier) => {
  for (const attribute of TIER_ATTRIBUTES.full) {
    row.toggleAttribute(attribute, TIER_ATTRIBUTES[tier].includes(attribute));
  }
};

/**
 * The widest tier whose content fits. `neededFor` says how wide the bar would
 * be at a tier; it is asked widest-first and not at all once a tier fits.
 * Compact is never measured — it is where the bar wraps instead of fitting.
 */
export const chooseToolbarTier = (
  available: number,
  neededFor: (tier: ToolbarTier) => number
): ToolbarTier => {
  for (const tier of TOOLBAR_TIERS) {
    if (tier === "compact") break;
    if (neededFor(tier) <= available) return tier;
  }
  return "compact";
};

/**
 * Keeps the toolbar row's tier attributes in step with the room the bar has
 * and the controls it holds.
 *
 * Measuring means trying each tier on the live DOM with the bar set to its
 * natural width, which makes a tier that does not fit show up as plain
 * overflow rather than as shrinking or wrapping. It all happens before paint —
 * in a layout effect, a ResizeObserver, or a MutationObserver callback — so
 * nothing between the first candidate and the final tier is ever drawn.
 *
 * Only the row's *width* is reacted to. Changing tier changes the row's height
 * when the bar wraps, and reacting to that would be measuring our own effect.
 */
export const useToolbarTier = (
  rowRef: RefObject<HTMLElement | null>,
  barRef: RefObject<HTMLElement | null>,
  enabled: boolean
) => {
  useLayoutEffect(() => {
    const row = rowRef.current;
    const bar = barRef.current;
    if (!enabled || !row || !bar) return;

    let disposed = false;

    const measure = () => {
      if (disposed) return;
      const available = Math.floor(bar.getBoundingClientRect().width);
      bar.style.width = "max-content";
      bar.style.flexWrap = "nowrap";
      const tier = chooseToolbarTier(available, (candidate) => {
        applyToolbarTier(row, candidate);
        return Math.ceil(bar.getBoundingClientRect().width);
      });
      bar.style.width = "";
      bar.style.flexWrap = "";
      applyToolbarTier(row, tier);
    };

    measure();

    let lastWidth = row.getBoundingClientRect().width;
    const resizes = new ResizeObserver((entries) => {
      const width =
        entries[0]?.contentRect.width ?? row.getBoundingClientRect().width;
      if (width === lastWidth) return;
      lastWidth = width;
      measure();
    });
    resizes.observe(row);

    // The controls arrive and change on their own schedule — the guidance
    // button once its query answers, the zoom label on every step — and each
    // changes how much room a tier needs. Attributes are left out because the
    // measurement above sets some itself.
    const mutations = new MutationObserver(measure);
    mutations.observe(bar, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    // Text measured against a fallback font is the wrong width.
    document.fonts?.ready.then(measure, () => undefined);

    return () => {
      disposed = true;
      resizes.disconnect();
      mutations.disconnect();
    };
  }, [rowRef, barRef, enabled]);
};
